package com.evoapps.evoshift.domain.usecase.marketplace

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.SwapPolicy
import com.evoapps.evoshift.domain.model.SwapRequest
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.NotifyRelevantEmployeesUseCase

class RequestShiftSwapUseCase(
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val notifyRelevantEmployeesUseCase: NotifyRelevantEmployeesUseCase,
    /**
     * SH-255: whether the initiator is hidden in the roster for the others; their shift then goes to
     * the managers first (never straight onto the marketplace with their name). Without it: not hidden.
     */
    private val rosterPrivacy: com.evoapps.evoshift.domain.privacy.RosterPrivacyRepository? = null
) {
    /** [note]: SH-165, the initiator's optional words for the manager and the colleague (trimmed, at most [SwapRequest.MAX_NOTE]). */
    suspend operator fun invoke(shiftId: String, initiatorUserId: String, targetUserId: String? = null, note: String? = null): Result<Unit> {
        return try {
            // 1. Pobierz dane: Zmiana i Profil Pracownika (żeby zapisać imię w migawce)
            val shift = shiftRepository.getShift(shiftId).getOrThrow()
            val initiator = userRepository.getUserProfile(initiatorUserId).getOrThrow()
            
            val targetUser = targetUserId?.let { 
                userRepository.getUserProfile(it).getOrNull() 
            }

            // 2. Pobierz ustawienia miejsca pracy (Polityka Wymian)
            val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
            val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()

            // Domyślna polityka (bezpieczna): B_APPROVAL
            val policy = workplace.settings.shiftSwapPolicy

            // 3. Ustal status początkowy na podstawie definicji z Twoich komentarzy
            // SH-255: someone hidden in the roster for the others offers a shift through the managers
            // (the rules refuse putting it on the marketplace directly).
            val hidden = targetUserId == null &&
                rosterPrivacy?.isHidden(workplaceId, initiatorUserId)?.getOrNull() == true
            val initialStatus = if (targetUserId != null || hidden) {
                // Jeśli wskazano osobę, to prośba o bezpośrednią zamianę
                SwapStatus.REQUESTED_BY_OWNER
            } else {
                when (policy) {
                    // Grupa "Open Pool": Zmiana trafia od razu na giełdę
                    SwapPolicy.A_DIRECT,
                    SwapPolicy.B_APPROVAL -> SwapStatus.AVAILABLE

                    // Grupa "Triage" (Bramkarz): Manager musi zaakceptować wrzucenie na giełdę
                    SwapPolicy.C_TRIAGE_DIRECT,
                    SwapPolicy.D_TRIAGE_APPROVAL -> SwapStatus.REQUESTED_BY_OWNER
                }
            }

            // 4. Zbuduj obiekt żądania
            val request = SwapRequest(
                status = initialStatus,
                initiatorUserId = initiatorUserId,
                initiatorName = initiator.displayName,
                candidateUserId = targetUserId,
                candidateName = targetUser?.displayName,
                managerNote = null,
                createdAt = AppClock.now().toString(),
                initiatorNote = note?.trim()?.takeIf { it.isNotEmpty() }?.take(SwapRequest.MAX_NOTE)
            )

            // 5. Zapisz w dokumencie zmiany
            shiftRepository.updateSwapRequest(shiftId, request).getOrThrow()

            // 6. Notifications
            if (initialStatus == SwapStatus.AVAILABLE) {
                notifyRelevantEmployeesUseCase(
                    workplaceId = workplaceId,
                    shift = shift,
                    title = "New Shift Available",
                    message = "${initiator.displayName} put their shift on the marketplace.",
                    notificationType = NotificationType.SHIFT_SWAP_REQUEST
                )
            } else if (targetUserId != null) {
                // Notify the target user
                userRepository.sendNotification(
                    Notification(
                        userId = targetUserId,
                        title = "Shift Swap Request",
                        message = "${initiator.displayName} wants to swap a shift with you.",
                        type = NotificationType.SHIFT_SWAP_REQUEST,
                        createdAt = request.createdAt,
                        data = NotificationData.forShift(shift, workplaceId, NotificationData.KIND to NotificationData.Kind.DIRECT, NotificationData.NAME to initiator.displayName, NotificationData.SUBJECT_USER_ID to initiatorUserId)
                    )
                )
            }

            // Always notify managers/owners about swap requests
            val allEmployees = userRepository.getAssignableEmployees(workplaceId).getOrDefault(emptyList())
            val managers = allEmployees.filter { employee ->
                val permission = employee.engagements[workplaceId]?.permission
                permission == UserPermission.OWNER || permission == UserPermission.MANAGER || permission == UserPermission.MANAGER_FINANCIAL
            }

            managers.forEach { manager ->
                userRepository.sendNotification(
                    Notification(
                        userId = manager.uid,
                        title = "New Swap Request",
                        message = "${initiator.displayName} requested a shift swap.",
                        type = NotificationType.SHIFT_SWAP_REQUEST,
                        createdAt = request.createdAt,
                        data = NotificationData.forShift(shift, workplaceId, NotificationData.KIND to NotificationData.Kind.REVIEW, NotificationData.NAME to initiator.displayName, NotificationData.SUBJECT_USER_ID to initiatorUserId)
                    )
                )
            }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
